import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HexColorPicker } from 'react-colorful';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import {
  useLandingPromoBackgroundJob,
  useLandingPromoBackgroundJobMeta,
  isLandingPromoBgJobBusy,
  configureLandingPromoImageJobs,
  tryResumeLandingPromoServerJob,
  startLandingPromoBackgroundRemoval,
  resetLandingPromoBackgroundJob,
  clearLandingPromoJobAfterSave,
  dismissLandingPromoBgError,
} from '../utils/landingPromotionBackgroundJobStore';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PromoForm = {
  enabled: boolean;
  eyebrow: string;
  headlinePrimary: string;
  headlineSecondary: string;
  promoCodePrefix: string;
  promoCode: string;
  buttonLabel: string;
  buttonLink: string;
  imageUrl: string;
  gradientFrom: string;
  gradientVia: string;
  gradientTo: string;
  accentColor: string;
  textColor: string;
  countdownEndsAt: string;
  countdownLabel: string;
  imageScalePercent: number;
};

function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toFullImageUrl(raw: string) {
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('/uploads/')) return `${API_BASE}${raw}`;
  return raw;
}

/** Normalize stored value for the visual picker (6-digit #hex). */
function toPickerHex(raw: string): string {
  const t = (raw || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(t)) return t.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(t)) {
    const r = t[1];
    const g = t[2];
    const b = t[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return '#64748b';
}

/** Isolated so parent form does not re-render on every background-removal progress tick */
function LandingPromoSettingsImageProgressPanel({
  bgStageLabel,
}: {
  bgStageLabel: (key: string) => string;
}) {
  const { t } = useTranslation();
  const job = useLandingPromoBackgroundJob();
  if (!isLandingPromoBgJobBusy(job)) return null;
  const isUpload = job.status === 'uploading';
  return (
    <div className="mt-4 space-y-2 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2 text-xs font-medium text-slate-800">
        <span>
          {isUpload
            ? t('backoffice.landing_promotion_image_uploading', {
                defaultValue: 'Uploading image…',
              })
            : t('backoffice.landing_promotion_image_processing', {
                defaultValue: 'Removing background on server…',
              })}
        </span>
        <span className="tabular-nums text-slate-600">
          {isUpload ? '—' : `${job.progress}%`}
        </span>
      </div>
      <div
        className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200"
        role="progressbar"
        aria-valuenow={isUpload ? 0 : job.progress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full bg-slate-900 transition-[width] duration-200 ease-out"
          style={{ width: isUpload ? '8%' : `${job.progress}%` }}
        />
      </div>
      {!isUpload ? (
        <p className="text-[11px] text-slate-600">{bgStageLabel(job.stageKey)}</p>
      ) : null}
      <p className="text-[11px] text-slate-500">
        {t('backoffice.landing_promotion_image_processing_background', {
          defaultValue:
            'Processing runs on the server. You can close this tab or use other workspace pages; open Landing promotion again to see the result or check the bar below.',
        })}
      </p>
    </div>
  );
}

export default function LandingPromotionSettingsPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch, auth } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const canEdit = (auth.permissions || []).includes('UI_SETTING:UPDATE')
    || auth.type === 'ADMIN';

  const bgMeta = useLandingPromoBackgroundJobMeta();

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [translationLocale, setTranslationLocale] = React.useState('fr');
  const [translationLoading, setTranslationLoading] = React.useState(false);
  const [translationSaving, setTranslationSaving] = React.useState(false);
  const [translationSuccess, setTranslationSuccess] = React.useState<string | null>(null);
  const [translationForm, setTranslationForm] = React.useState({
    eyebrow: '',
    headlinePrimary: '',
    headlineSecondary: '',
    promoCodePrefix: '',
    promoCode: '',
    buttonLabel: '',
    countdownLabel: '',
  });

  const [form, setForm] = React.useState<PromoForm>({
    enabled: false,
    eyebrow: '',
    headlinePrimary: '',
    headlineSecondary: '',
    promoCodePrefix: '',
    promoCode: '',
    buttonLabel: '',
    buttonLink: '#products',
    imageUrl: '',
    gradientFrom: '#7f1d1d',
    gradientVia: '#b91c1c',
    gradientTo: '#450a0a',
    accentColor: '#2563eb',
    textColor: '#ffffff',
    countdownEndsAt: '',
    countdownLabel: 'Offer ends in',
    imageScalePercent: 100,
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/settings/landing-promotion`);
      if (!res.ok) throw new Error('load');
      const data = (await res.json()) as PromoForm;
      setForm({
        enabled: Boolean(data.enabled),
        eyebrow: data.eyebrow ?? '',
        headlinePrimary: data.headlinePrimary ?? '',
        headlineSecondary: data.headlineSecondary ?? '',
        promoCodePrefix: data.promoCodePrefix ?? '',
        promoCode: data.promoCode ?? '',
        buttonLabel: data.buttonLabel ?? '',
        buttonLink: data.buttonLink ?? '#products',
        imageUrl: data.imageUrl ?? '',
        gradientFrom: data.gradientFrom ?? '#7f1d1d',
        gradientVia: data.gradientVia ?? '#b91c1c',
        gradientTo: data.gradientTo ?? '#450a0a',
        accentColor: data.accentColor ?? '#2563eb',
        textColor: data.textColor ?? '#ffffff',
        countdownEndsAt: data.countdownEndsAt ?? '',
        countdownLabel: data.countdownLabel ?? 'Offer ends in',
        imageScalePercent:
          typeof data.imageScalePercent === 'number' &&
          Number.isFinite(data.imageScalePercent)
            ? Math.min(1000, Math.max(50, Math.round(data.imageScalePercent)))
            : 100,
      });
    } catch {
      setError(t('backoffice.landing_promotion_load_error', { defaultValue: 'Unable to load settings.' }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    configureLandingPromoImageJobs(API_BASE, (url, init) =>
      globalAuthorizedFetch(url as string, init),
    );
    tryResumeLandingPromoServerJob();
  }, [globalAuthorizedFetch]);

  React.useEffect(() => {
    if (bgMeta.status !== 'completed' || !bgMeta.serverImageUrl) return;
    setForm((p) => {
      if (p.imageUrl === bgMeta.serverImageUrl) return p;
      return { ...p, imageUrl: bgMeta.serverImageUrl! };
    });
  }, [bgMeta.status, bgMeta.serverImageUrl]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      setTranslationLoading(true);
      try {
        const url = new URL(`${API_BASE}/settings/landing-promotion/translations`);
        url.searchParams.set('locale', translationLocale);
        const res = await globalAuthorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as { translations: typeof translationForm };
        if (!cancelled) {
          setTranslationForm({
            eyebrow: data.translations.eyebrow || '',
            headlinePrimary: data.translations.headlinePrimary || '',
            headlineSecondary: data.translations.headlineSecondary || '',
            promoCodePrefix: data.translations.promoCodePrefix || '',
            promoCode: data.translations.promoCode || '',
            buttonLabel: data.translations.buttonLabel || '',
            countdownLabel: data.translations.countdownLabel || '',
          });
        }
      } finally {
        if (!cancelled) setTranslationLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [globalAuthorizedFetch, translationLocale]);

  const saveTranslations = async () => {
    setTranslationSaving(true);
    setTranslationSuccess(null);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/landing-promotion/translations`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          locale: translationLocale,
          translations: translationForm,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = (body as { message?: string }).message || 'Unable to save translations.';
        throw new Error(msg);
      }
      setTranslationSuccess('Translation saved successfully.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save translations.');
    } finally {
      setTranslationSaving(false);
    }
  };

  const bgStageLabel = React.useCallback(
    (key: string) => {
      if (!key) {
        return t('backoffice.landing_promotion_stage_start', { defaultValue: 'Starting…' });
      }
      if (key.startsWith('fetch:')) {
        return t('backoffice.landing_promotion_stage_fetch', {
          defaultValue: 'Loading AI model (first visit may take a while)…',
        });
      }
      if (key.startsWith('compute:decode')) {
        return t('backoffice.landing_promotion_stage_decode', {
          defaultValue: 'Preparing image…',
        });
      }
      if (key.startsWith('compute:inference')) {
        return t('backoffice.landing_promotion_stage_inference', {
          defaultValue: 'Removing background…',
        });
      }
      if (key.startsWith('compute:mask')) {
        return t('backoffice.landing_promotion_stage_mask', { defaultValue: 'Refining edges…' });
      }
      if (key.startsWith('compute:encode')) {
        return t('backoffice.landing_promotion_stage_encode', { defaultValue: 'Encoding PNG…' });
      }
      return t('backoffice.landing_promotion_stage_working', { defaultValue: 'Processing…' });
    },
    [t],
  );

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!file) {
      resetLandingPromoBackgroundJob();
      return;
    }
    const result = await startLandingPromoBackgroundRemoval(file);
    if (!result.ok) {
      if (result.reason === 'busy') {
        setError(
          t('backoffice.landing_promotion_bg_busy', {
            defaultValue:
              'Background removal is already running. Wait for it to finish.',
          }),
        );
      } else if (result.reason === 'network' || result.reason === 'server') {
        setError(
          t('backoffice.landing_promotion_bg_start_error', {
            defaultValue:
              'Could not start server processing. Check your connection and try again.',
          }),
        );
      } else if (result.validation === 'not_image') {
        setError(
          t('backoffice.landing_promotion_image_not_image', {
            defaultValue: 'Please choose an image file.',
          }),
        );
      } else if (result.validation === 'too_large') {
        setError(
          t('backoffice.landing_promotion_image_too_large', {
            defaultValue: 'Image too large for processing (max 12 MB).',
          }),
        );
      }
      return;
    }
    setError(null);
    setSuccess(null);
  };

  const setField =
    <K extends keyof PromoForm>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = e.target.type === 'checkbox'
        ? (e.target as HTMLInputElement).checked
        : e.target.value;
      setForm((prev) => ({ ...prev, [key]: v } as PromoForm));
    };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit || isLandingPromoBgJobBusy(bgMeta)) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const imageUrl = form.imageUrl;

      const res = await globalAuthorizedFetch(`${API_BASE}/settings/landing-promotion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          imageUrl,
          imageScalePercent: Math.min(
            1000,
            Math.max(50, Math.round(Number(form.imageScalePercent) || 100)),
          ),
        }),
      });
      if (!res.ok) throw new Error('save');
      const next = (await res.json()) as PromoForm;
      setForm((prev) => ({ ...prev, ...next, imageUrl: next.imageUrl || imageUrl }));
      clearLandingPromoJobAfterSave();
      setSuccess(t('backoffice.landing_promotion_saved', { defaultValue: 'Landing promotion saved.' }));
    } catch {
      setError(t('backoffice.landing_promotion_save_error', { defaultValue: 'Unable to save.' }));
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = toFullImageUrl(form.imageUrl);
  const displayPreview = previewUrl;

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <div>
          <h1 className="workspace-page-title">
            {t('backoffice.landing_promotion_title', { defaultValue: 'Landing promotion' })}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {t('backoffice.landing_promotion_subtitle', {
              defaultValue:
                'This is the first screen shoppers see: bold offer, promo code, and product photo. When enabled, it replaces the hero carousel on the home page.',
            })}
          </p>
        </div>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        {bgMeta.status === 'failed' ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <span className="min-w-0 flex-1">
              {bgMeta.errorCode === 'job_not_found'
                ? t('backoffice.landing_promotion_bg_job_expired', {
                    defaultValue:
                      'The background job was not found (it may have expired after a server restart). Start a new upload.',
                  })
                : t('backoffice.landing_promotion_bg_remove_error', {
                    defaultValue:
                      'Could not remove the background on the server. Try another image or a smaller file.',
                  })}
            </span>
            <button
              type="button"
              className="shrink-0 rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-semibold text-red-900 hover:bg-red-100"
              onClick={() => dismissLandingPromoBgError()}
            >
              {t('common.dismiss', { defaultValue: 'Dismiss' })}
            </button>
          </div>
        ) : null}
        {success ? <p className="text-sm text-emerald-600">{success}</p> : null}

        {loading ? (
          <p className="text-sm text-slate-500">{t('common.loading', { defaultValue: 'Loading…' })}</p>
        ) : (
          <form onSubmit={handleSave} className="max-w-3xl space-y-8">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={setField('enabled')}
                disabled={!canEdit}
                className="rounded border-slate-300"
              />
              {t('backoffice.landing_promotion_enabled', { defaultValue: 'Show promotion on storefront home (replaces carousel)' })}
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">
                  {t('backoffice.landing_promotion_eyebrow', { defaultValue: 'Eyebrow (small caps line)' })}
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={form.eyebrow}
                  onChange={setField('eyebrow')}
                  disabled={!canEdit}
                  maxLength={120}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">
                  {t('backoffice.landing_promotion_button', { defaultValue: 'Button label' })}
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={form.buttonLabel}
                  onChange={setField('buttonLabel')}
                  disabled={!canEdit}
                  maxLength={80}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                {t('backoffice.landing_promotion_headline_1', { defaultValue: 'Main headline (e.g. 15% OFF)' })}
              </label>
              <input
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold"
                value={form.headlinePrimary}
                onChange={setField('headlinePrimary')}
                disabled={!canEdit}
                maxLength={200}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                {t('backoffice.landing_promotion_headline_2', { defaultValue: 'Sub headline (e.g. READY-TO-GIFT COOKIES)' })}
              </label>
              <input
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={form.headlineSecondary}
                onChange={setField('headlineSecondary')}
                disabled={!canEdit}
                maxLength={200}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">
                  {t('backoffice.landing_promotion_code_prefix', { defaultValue: 'Promo prefix (e.g. Use code:)' })}
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={form.promoCodePrefix}
                  onChange={setField('promoCodePrefix')}
                  disabled={!canEdit}
                  maxLength={80}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">
                  {t('backoffice.landing_promotion_code', { defaultValue: 'Promo code' })}
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
                  value={form.promoCode}
                  onChange={setField('promoCode')}
                  disabled={!canEdit}
                  maxLength={40}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">
                {t('backoffice.landing_promotion_link', { defaultValue: 'Button link (#products, /path, or https://)' })}
              </label>
              <input
                className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
                value={form.buttonLink}
                onChange={setField('buttonLink')}
                disabled={!canEdit}
                maxLength={500}
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="mb-3 text-xs font-semibold text-slate-700">
                {t('backoffice.landing_promotion_countdown', {
                  defaultValue: 'Optional countdown (storefront)',
                })}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">
                    {t('backoffice.landing_promotion_countdown_end', {
                      defaultValue: 'End date & time (local)',
                    })}
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={toDatetimeLocalValue(form.countdownEndsAt)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((p) => ({
                        ...p,
                        countdownEndsAt: v ? new Date(v).toISOString() : '',
                      }));
                    }}
                    disabled={!canEdit}
                  />
                  {form.countdownEndsAt ? (
                    <button
                      type="button"
                      className="text-xs font-medium text-slate-600 underline hover:text-slate-900"
                      onClick={() => setForm((p) => ({ ...p, countdownEndsAt: '' }))}
                      disabled={!canEdit}
                    >
                      {t('backoffice.landing_promotion_clear_countdown', {
                        defaultValue: 'Clear countdown',
                      })}
                    </button>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">
                    {t('backoffice.landing_promotion_countdown_label', {
                      defaultValue: 'Countdown label',
                    })}
                  </label>
                  <input
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={form.countdownLabel}
                    onChange={setField('countdownLabel')}
                    disabled={!canEdit}
                    maxLength={100}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="mb-3 text-xs font-semibold text-slate-700">
                {t('backoffice.landing_promotion_image', { defaultValue: 'Hero product image (right side on desktop)' })}
              </p>
              <p className="mb-3 text-[11px] leading-relaxed text-slate-600">
                {t('backoffice.landing_promotion_image_bg_hint', {
                  defaultValue:
                    'The background is removed on the server (transparent PNG). You can close this tab or use other apps — when it is done, reopen Landing promotion or save. The first server run may download the AI model (~40–80 MB, then cached on the server).',
                })}
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <input
                  type="file"
                  accept="image/*"
                  disabled={!canEdit || isLandingPromoBgJobBusy(bgMeta)}
                  onChange={handleImageFileChange}
                  className="text-sm"
                />
                <input
                  className="min-w-[200px] flex-1 rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs"
                  placeholder="/uploads/..."
                  value={form.imageUrl}
                  onChange={setField('imageUrl')}
                  disabled={!canEdit || isLandingPromoBgJobBusy(bgMeta)}
                />
              </div>
              <LandingPromoSettingsImageProgressPanel bgStageLabel={bgStageLabel} />
              {bgMeta.status === 'completed' ? (
                <p className="mt-3 text-xs font-medium text-emerald-800">
                  {t('backoffice.landing_promotion_image_ready', {
                    defaultValue:
                      'Background removed on the server. Save the promotion to publish this image.',
                  })}
                </p>
              ) : null}
              {displayPreview ? (
                <img
                  src={displayPreview}
                  alt=""
                  className="mt-3 max-h-40 rounded-lg border border-slate-200 object-contain bg-[repeating-conic-gradient(#e2e8f0_0%_25%,#f8fafc_0%_50%)_50%_50%/16px_16px]"
                />
              ) : null}

              <div className="mt-4 space-y-2 border-t border-slate-200 pt-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label
                    htmlFor="landing-promo-image-scale"
                    className="text-xs font-semibold text-slate-700"
                  >
                    {t('backoffice.landing_promotion_image_scale', {
                      defaultValue: 'Storefront image size',
                    })}
                  </label>
                  <span className="font-mono text-sm font-bold tabular-nums text-slate-900">
                    {form.imageScalePercent}%
                  </span>
                </div>
                <input
                  id="landing-promo-image-scale"
                  type="range"
                  min={50}
                  max={1000}
                  step={1}
                  value={form.imageScalePercent}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      imageScalePercent: Math.min(
                        1000,
                        Math.max(50, Number(e.target.value)),
                      ),
                    }))
                  }
                  disabled={!canEdit}
                  className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-slate-900 disabled:opacity-50"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-[11px] text-slate-600">
                    {t('backoffice.landing_promotion_image_scale_exact', {
                      defaultValue: 'Exact %',
                    })}
                  </label>
                  <input
                    type="number"
                    min={50}
                    max={1000}
                    step={1}
                    value={form.imageScalePercent}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      if (!Number.isFinite(n)) return;
                      setForm((p) => ({
                        ...p,
                        imageScalePercent: Math.min(1000, Math.max(50, Math.round(n))),
                      }));
                    }}
                    disabled={!canEdit}
                    className="w-24 rounded border border-slate-300 px-2 py-1 font-mono text-xs"
                  />
                </div>
                <p className="text-[11px] leading-relaxed text-slate-500">
                  {t('backoffice.landing_promotion_image_scale_hint', {
                    defaultValue:
                      '100% is the baseline layout maximum. Use up to 1000% for a very large product (may be clipped by the hero edges). Save to apply.',
                  })}
                </p>
              </div>
            </div>

            <div>
              <p className="mb-1 text-xs font-semibold text-slate-700">
                {t('backoffice.landing_promotion_colors', {
                  defaultValue: 'Background gradient & CTA',
                })}
              </p>
              <p className="mb-4 text-[11px] leading-relaxed text-slate-500">
                {t('backoffice.landing_promotion_colors_hint', {
                  defaultValue:
                    'Pick colors visually below. The hex value updates automatically — use Copy if you need it elsewhere.',
                })}
              </p>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {([
                  ['gradientFrom', t('backoffice.landing_promotion_c1', { defaultValue: 'Gradient start' })],
                  ['gradientVia', t('backoffice.landing_promotion_c2', { defaultValue: 'Gradient middle' })],
                  ['gradientTo', t('backoffice.landing_promotion_c3', { defaultValue: 'Gradient end' })],
                  ['accentColor', t('backoffice.landing_promotion_cta', { defaultValue: 'CTA button' })],
                  ['textColor', t('backoffice.landing_promotion_text', { defaultValue: 'Text' })],
                ] as const).map(([key, label]) => {
                  const hex = toPickerHex(form[key]);
                  return (
                    <div
                      key={key}
                      className="workspace-stat-card"
                    >
                      <label
                        htmlFor={`promo-color-${key}`}
                        className="mb-3 block text-xs font-bold uppercase tracking-wide text-slate-800"
                      >
                        {label}
                      </label>
                      <div
                        id={`promo-color-${key}`}
                        className={
                          canEdit ? '' : 'pointer-events-none select-none opacity-60'
                        }
                      >
                        <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                          <HexColorPicker
                            color={hex}
                            onChange={(next) =>
                              setForm((p) => ({
                                ...p,
                                [key]: next.toLowerCase(),
                              }))
                            }
                            style={{
                              width: '100%',
                              height: 'min(200px, 46vw)',
                              maxHeight: 200,
                            }}
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex items-stretch gap-2">
                        <span
                          className="w-11 shrink-0 rounded-lg border border-slate-200 shadow-inner"
                          style={{ backgroundColor: hex }}
                          title={hex}
                          aria-hidden
                        />
                        <code className="flex min-w-0 flex-1 items-center truncate rounded-lg bg-slate-50 px-3 py-2 font-mono text-[11px] text-slate-700">
                          {hex}
                        </code>
                        <button
                          type="button"
                          disabled={!canEdit}
                          className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                          onClick={() => {
                            void navigator.clipboard?.writeText(hex);
                          }}
                        >
                          {t('backoffice.landing_promotion_copy_hex', {
                            defaultValue: 'Copy',
                          })}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                  Dynamic content translations
                </p>
                <select
                  value={translationLocale}
                  onChange={(e) => {
                    setTranslationLocale(e.target.value);
                    setTranslationSuccess(null);
                  }}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs"
                >
                  <option value="fr">French (fr)</option>
                  <option value="es">Spanish (es)</option>
                  <option value="de">German (de)</option>
                </select>
              </div>
              {translationLoading ? (
                <p className="text-xs text-slate-500">Loading translations...</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {(
                    [
                      ['eyebrow', 'Localized eyebrow'],
                      ['headlinePrimary', 'Localized main headline'],
                      ['headlineSecondary', 'Localized sub headline'],
                      ['promoCodePrefix', 'Localized promo prefix'],
                      ['promoCode', 'Localized promo code'],
                      ['buttonLabel', 'Localized button label'],
                      ['countdownLabel', 'Localized countdown label'],
                    ] as const
                  ).map(([k, label]) => (
                    <label key={k} className="text-xs font-medium text-slate-700">
                      {label}
                      <input
                        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={translationForm[k]}
                        onChange={(e) => {
                          setTranslationForm((p) => ({ ...p, [k]: e.target.value }));
                          setTranslationSuccess(null);
                        }}
                      />
                    </label>
                  ))}
                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      onClick={saveTranslations}
                      disabled={translationSaving}
                      className="rounded-md border border-brand-500 bg-white px-4 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-60"
                    >
                      {translationSaving ? 'Saving translation...' : 'Save translation'}
                    </button>
                    {translationSuccess ? (
                      <p className="mt-2 text-xs font-medium text-emerald-700">
                        {translationSuccess}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}
            </div>

            {canEdit ? (
              <button
                type="submit"
                disabled={saving || isLandingPromoBgJobBusy(bgMeta)}
                className="rounded-lg bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {saving
                  ? t('common.saving', { defaultValue: 'Saving…' })
                  : t('backoffice.landing_promotion_save', { defaultValue: 'Save promotion' })}
              </button>
            ) : (
              <p className="text-sm text-amber-800">
                {t('backoffice.landing_promotion_no_permission', {
                  defaultValue: 'You do not have permission to update UI settings.',
                })}
              </p>
            )}
          </form>
        )}
      </main>
    </MainLayout>
  );
}
